<script>
import BoardResource from '../resources/board'
import LocationResource from '../resources/location'
import { timeDateFormattedPretty } from '@/util'

export default {
    name: 'DisplayScreen',
    components: {},
    computed: {
        ranksPodium() {
            return this.rankResources.slice(0, 3)
        },
        ranksRest() {
            return this.rankResources.slice(3)
        },
        isPoints() {
            return !!this.selectedBoardResource && this.selectedBoardResource.attributes.calculation === 'POINTS'
        },
        boardWindowLabel() {
            const board = this.selectedBoardResource
            if (!board) {
                return ''
            }
            if (board.attributes.since) {
                return `Since ${this.timeDateFormattedPretty(board.attributes.since)}`
            }
            return board.attributes.time_frame_pretty || 'All time'
        },
        externalBoardId() {
            return this.boardId || this.$route.params.boardId
        },
        externalInterval() {
            return this.interval || this.$route.query.reloadInterval
        },
        externalCount() {
            return this.count || this.$route.query.itemsToShow
        },
        externalExpandDesc() {
            return this.desc || this.$route.query.desc
        },
        boards() {
            if (!this.selectedLocationResource) {
                return this.boardResources
            }

            return this.boardResources.filter((resource) => resource.attributes.location == this.selectedLocationResource.id)
        }
    },
    props: {
        boardId: String,
        interval: [String, Number],
        count: [String, Number],
        desc: Boolean
    },
    data() {
        return {
            loading: true,
            editMode: true,
            expandDesc: false,
            locationResources: [],
            selectedLocationResource: null,
            boardResources: [],
            selectedBoardResource: null,
            rankResources: [],
            reloadInterval: 60,
            reloadIntervalId: null,
            itemsToShow: 15,
            boardInfoModalVisible: false,
        }
    },
    watch: {
        selectedBoardResource() {
            this.updateBoardResults(this.selectedBoardResource)
        },
        selectedLocationResource() {
            this.selectedBoardResource = this.boards[0]
        }
    },
    methods: {
        timeDateFormattedPretty,
        metaFor(item) {
            const attrs = item.attributes
            const parts = [attrs.car_name || attrs.car]
            if (attrs.fuel) {
                parts.push(`${parseInt(attrs.fuel)}L`)
            }
            if (attrs.tire) {
                parts.push(`"${attrs.tire}"`)
            }
            if (attrs.ballast) {
                parts.push(`bst ${attrs.ballast}`)
            }
            return parts.filter(Boolean).join('  ·  ')
        },
        async onSubmit() {
            await this.updateBoardResults(this.selectedBoardResource)

            this.editMode = false
            this.$router.replace({
                name: this.$route.name,
                params: {
                    boardId: this.selectedBoardResource.id,
                },
                query: {
                    reloadInterval: this.reloadInterval,
                    itemsToShow: this.itemsToShow,
                },
            })
        },
        async updateBoardResults(boardResource, options = {}) {
            if (options.clearCurrent !== false) {
                this.rankResources = []
            }

            if (boardResource.attributes.calculation === 'POINTS') {
                const result = await boardResource.getResults({ page_size: this.itemsToShow })
                this.rankResources = result.resources
            } else {
                const result = await boardResource.getResults({ count: this.itemsToShow })
                this.rankResources = result.resources
            }
            window.scroll(0, 0)
        },
    },
    async mounted() {
        document.documentElement.setAttribute('data-theme', 'dark')

        if (this.externalBoardId) {
            const board = await BoardResource.detail(this.externalBoardId)
            this.boardResources = [board]
            this.selectedBoardResource = board
            this.editMode = false

            if (this.externalInterval) {
                this.reloadInterval = this.externalInterval
            }

            if (this.externalCount) {
                this.itemsToShow = this.externalCount
            }

            if (this.externalExpandDesc) {
                this.expandDesc = this.externalExpandDesc
            }
        } else {
            const [locations, boards] = await Promise.all([
                await LocationResource.all(),
                await BoardResource.all()
            ])

            this.locationResources = locations
            this.boardResources = boards
            this.selectedBoardResource = this.boardResources[0]
        }

        this.reloadIntervalId = setInterval(() => {
            this.updateBoardResults(this.selectedBoardResource, { clearCurrent: false })
        }, this.reloadInterval * 1000)

        this.loading = false
    },
    beforeDestroy() {
        clearInterval(this.reloadIntervalId)
    },
}
</script>

<template>
    <div class="board" v-if="!loading">
        <form v-if="editMode" class="board__setup" @submit.prevent="onSubmit">
            <div class="board__setupcard">
                <div class="sos-eyebrow">Board display</div>
                <h2 class="board__setuptitle">Configure this screen</h2>

                <label class="board__field">
                    <span>Location</span>
                    <b-form-select v-model="selectedLocationResource">
                        <b-form-select-option :value="null">Any</b-form-select-option>
                        <b-form-select-option v-for="(locationResource, i) in locationResources" :key="i" :value="locationResource">{{ locationResource }}</b-form-select-option>
                    </b-form-select>
                </label>

                <label class="board__field">
                    <span>Leaderboard</span>
                    <b-form-select v-model="selectedBoardResource">
                        <b-form-select-option v-for="(boardResource, i) in boards" :key="i" :value="boardResource">{{ boardResource }}</b-form-select-option>
                    </b-form-select>
                </label>

                <label class="board__field">
                    <span>Rows to show</span>
                    <b-form-spinbutton v-model="itemsToShow" min="1" max="100" />
                </label>

                <label class="board__field">
                    <span>Refresh every {{ reloadInterval }}s</span>
                    <b-form-input type="range" v-model="reloadInterval" min="10" step="10" max="600" />
                </label>

                <b-button type="submit" block variant="secondary" class="mt-2">Launch display</b-button>
            </div>
        </form>

        <div v-else-if="selectedBoardResource" class="board__stage">
            <header class="board__bar">
                <div class="board__ident">
                    <div class="sos-eyebrow">{{ isPoints ? 'Championship' : 'Leaderboard' }}</div>
                    <h1 class="board__name">{{ selectedBoardResource }}</h1>
                </div>
                <div class="board__meta">
                    <span class="board__window">{{ boardWindowLabel }}</span>
                    <b-button
                        v-if="selectedBoardResource.attributes.description"
                        size="sm"
                        variant="outline-light"
                        @click="boardInfoModalVisible = true"
                    ><b-icon icon="info-circle" /> Info</b-button>
                    <span class="board__live"><span class="board__livedot"></span> Live</span>
                </div>
            </header>

            <p v-if="expandDesc && selectedBoardResource.attributes.description" class="board__desc">
                {{ selectedBoardResource.attributes.description }}
            </p>

            <template v-if="rankResources.length > 0">
                <div class="board__podium">
                    <div
                        v-for="item in ranksPodium"
                        :key="item.id"
                        class="board__slot"
                        :class="{ 'board__slot--lead': item.attributes.rank == 1 }"
                    >
                        <div class="board__slotpos">P{{ item.attributes.rank }}</div>
                        <img v-if="item.attributes.flag_url" :src="item.attributes.flag_url" class="board__flag" />
                        <div class="board__slotname">{{ item.attributes.name || item.attributes.display_name }}</div>
                        <div class="board__slottime">{{ isPoints ? `${item.attributes.points} pts` : item.attributes.hms }}</div>
                        <div class="board__slotmeta">{{ metaFor(item) }}</div>
                        <a
                            v-if="item.attributes.display_social"
                            class="board__social"
                            :href="`https://instagram.com/${item.attributes.display_social}`"
                            target="_blank"
                        ><b-icon icon="instagram" /> {{ item.attributes.display_social }}</a>
                    </div>
                </div>

                <div class="board__rows" v-if="ranksRest.length > 0">
                    <div class="board__row" v-for="item in ranksRest" :key="item.id">
                        <span class="board__pos">{{ item.attributes.rank }}</span>
                        <img v-if="item.attributes.flag_url" :src="item.attributes.flag_url" class="board__rowflag" />
                        <span class="board__rowname">{{ item.attributes.name || item.attributes.display_name }}</span>
                        <span class="board__rowmeta">{{ metaFor(item) }}</span>
                        <span class="board__rowtime">{{ isPoints ? `${item.attributes.points} pts` : item.attributes.hms }}</span>
                    </div>
                </div>
            </template>

            <div v-else class="board__empty">No laps have been recorded for this leaderboard yet.</div>
        </div>

        <b-modal v-model="boardInfoModalVisible" :title="String(selectedBoardResource)" no-fade hide-footer centered>
            {{ selectedBoardResource.attributes.description }}
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>
.board {
    min-height: 100vh;
    color: var(--sos-display-ink);
    font-family: var(--sos-font);
    padding: 2.2vw 2.6vw;
    background:
        radial-gradient(120% 80% at 85% -10%, rgba(255, 255, 255, 0.06), transparent 60%),
        radial-gradient(90% 60% at 0% 110%, var(--sos-accent-soft), transparent 55%),
        linear-gradient(180deg, var(--sos-display-bg-2), var(--sos-display-bg));
}

.board__setup {
    display: flex;
    justify-content: center;
    padding-top: 12vh;
}
.board__setupcard {
    width: 100%;
    max-width: 420px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: var(--sos-r-lg);
    padding: var(--sos-space-6);
}
.board__setuptitle {
    font-family: var(--sos-font-head);
    font-weight: 700;
    margin: 0 0 var(--sos-space-5);
}
.board__field {
    display: block;
    margin-bottom: var(--sos-space-4);

    span {
        display: block;
        font-family: var(--sos-font-head);
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        font-size: var(--sos-fs-eyebrow);
        color: rgba(255, 255, 255, 0.7);
        margin-bottom: 6px;
    }

    ::v-deep .custom-select,
    ::v-deep .form-control,
    ::v-deep .b-form-spinbutton {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.18);
        color: #fff;
    }
}

.board__bar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--sos-space-4);
    border-bottom: 2px solid rgba(255, 255, 255, 0.12);
    padding-bottom: 1.2vw;
    margin-bottom: 1.6vw;
}
.board__ident .sos-eyebrow {
    color: rgba(255, 255, 255, 0.55);
}
.board__name {
    font-family: var(--sos-font-head);
    font-weight: 700;
    font-size: 3.4vw;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.01em;
    margin: 0.2vw 0 0;
}
.board__meta {
    display: flex;
    align-items: center;
    gap: var(--sos-space-4);
    flex: none;
}
.board__window {
    font-family: var(--sos-font-head);
    font-weight: 600;
    font-size: 1.1vw;
    color: rgba(255, 255, 255, 0.75);
}
.board__live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--sos-font-head);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 0.85vw;
    color: rgba(255, 255, 255, 0.7);
}
.board__livedot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--sos-good);
    animation: board-pulse 1.6s ease-in-out infinite;
}
@keyframes board-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
}

.board__desc {
    font-size: 1.1vw;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 1.4vw;
}

.board__podium {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.4vw;
    margin-bottom: 1.6vw;
}
.board__slot {
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: var(--sos-r-lg);
    padding: 1.6vw 1.4vw;
    text-align: center;
}
.board__slot--lead {
    border-color: var(--sos-accent);
    box-shadow: 0 0 0 2px var(--sos-accent), 0 18px 50px rgba(0, 0, 0, 0.4);
}
.board__slotpos {
    font-family: var(--sos-font-head);
    font-weight: 700;
    font-size: 2vw;
    color: rgba(255, 255, 255, 0.55);
}
.board__slot--lead .board__slotpos {
    color: var(--sos-accent);
}
.board__flag {
    height: 1.6vw;
    border-radius: 4px;
    margin: 0.4vw 0;
}
.board__slotname {
    font-family: var(--sos-font-head);
    font-weight: 700;
    font-size: 1.7vw;
}
.board__slottime {
    font-family: var(--sos-font-mono);
    font-variant-numeric: tabular-nums;
    font-size: 1.8vw;
    font-weight: 700;
    color: #fff;
    margin-top: 0.2vw;
}
.board__slotmeta {
    font-size: 0.95vw;
    color: rgba(255, 255, 255, 0.6);
    margin-top: 0.2vw;
}
.board__social {
    display: inline-block;
    margin-top: 0.4vw;
    font-size: 0.9vw;
    color: rgba(255, 255, 255, 0.7);
}

.board__rows {
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: var(--sos-r-lg);
    overflow: hidden;
}
.board__row {
    display: flex;
    align-items: center;
    gap: 1.2vw;
    padding: 0.9vw 1.4vw;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);

    &:last-child {
        border-bottom: 0;
    }
    &:nth-child(-n + 3) {
        background: rgba(255, 255, 255, 0.05);
        font-weight: 700;
    }
}
.board__pos {
    font-family: var(--sos-font-head);
    font-weight: 700;
    font-size: 1.4vw;
    color: rgba(255, 255, 255, 0.55);
    min-width: 2.4ch;
    text-align: right;
    font-variant-numeric: tabular-nums;
}
.board__rowflag {
    height: 1.2vw;
    border-radius: 3px;
}
.board__rowname {
    flex: 1;
    min-width: 0;
    font-family: var(--sos-font-head);
    font-weight: 600;
    font-size: 1.5vw;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.board__rowmeta {
    font-size: 1vw;
    color: rgba(255, 255, 255, 0.55);
    white-space: nowrap;
}
.board__rowtime {
    font-family: var(--sos-font-mono);
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    font-size: 1.5vw;
    color: #fff;
}

.board__empty {
    text-align: center;
    padding: 8vh 2vw;
    font-size: 1.4vw;
    color: rgba(255, 255, 255, 0.6);
    border: 1px dashed rgba(255, 255, 255, 0.2);
    border-radius: var(--sos-r-lg);
}
</style>
